/**
 * Character Roster Sidebar - Party Display
 *
 * File: Mythbound.App/src/components/campaign/CharacterRosterSidebar.tsx
 * Created: 2026-01-06
 * Last Modified: 2026-01-06
 *
 * PURPOSE:
 * Right sidebar displaying campaign character roster with current player's
 * character highlighted. Always visible on campaign page, collapsible, mobile slide-out.
 *
 * UI LAYOUT:
 * ┌─────────────────────┐
 * │ Your Character      │
 * ├─────────────────────┤
 * │ [Large portrait]    │
 * │ Gandalf (Level 5)   │
 * │ Wizard              │
 * │ HP: 40/40           │
 * │ AC: 12              │
 * │ [Visibility] [Edit] │
 * ├─────────────────────┤
 * │ Party Members       │
 * ├─────────────────────┤
 * │ [Mini][Name] Level  │ ← Frodo
 * │ [Mini][Name] Level  │ ← Aragorn
 * │ [+] Add Character   │
 * └─────────────────────┘
 *
 * KEY FEATURES:
 * - Player's character shown large at top with full stats
 * - Party members as mini-icons with name/level, hoverable for preview
 * - Stats displayed only if marked "visible to campaign"
 * - Clicking party member highlights them (shows in main view, not sidebar)
 * - Add Character button opens CharacterCreationModal
 * - Settings gear icon opens CharacterVisibilitySettings
 * - Collapsible (desktop) and slide-out (mobile)
 *
 * VISUAL DESIGN:
 * - Clean tech UI (not parchment) - functional element
 * - Dark background (#1a1a1a)
 * - Gold accents for player's character
 * - Subtle hover states on mini-icons
 * - Smooth collapse/expand animation
 *
 * VISIBILITY FILTERING:
 * - Only show stats marked "visible to campaign"
 * - Render the backend's authorized projection as returned; GM status does not bypass redaction
 *
 * RESPONSIVENESS:
 * - Desktop: Fixed right sidebar (280px width), collapsible
 * - Tablet: Collapsible with toggle button
 * - Mobile: Slide-out drawer from right
 *
 * [FUTURE CODESPACE]
 * - Sticky sidebar during active session (persists across pages)
 * - Turn order highlighting (Phase 2)
 * - Status effects display (Phase 2)
 * - Conditions (poisoned, blessed, etc.) (Phase 2)
 * - Hover stat comparison (Phase 2)
 *
 * WHO: Rendered from `CampaignPage.tsx`, always mounted (not conditional).
 * WHAT: Displays the authorized roster supplied by `CharacterContext`; this
 * keeps the page count, selected character and sidebar on one request result.
 * WHY (PRD001A 001A.4; hidden-vs-unknown mechanism updated R6, 2026-09-23):
 * `hp`/`max_hp`/`armor_class` (and, defensively, `character_class`/`level`)
 * are `T | null | undefined` per the frozen contract — an ABSENT key
 * (`undefined`) means "hidden by the owner's visibility setting," a
 * PRESENT `null` means "genuinely never set," and a present real value
 * (including `0`) is a real fact. `classifyCharacterField`/
 * `formatCharacterField` (used below) implement exactly this three-way
 * check, undefined before null. Owner formatting and controls require the explicit server `is_owner` boolean; order and GM status do not grant ownership.
 *
 * Pipeline: CampaignPage → CharacterContext → CharacterService → Backend
 * Dependencies: React, useState, useEffect
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useCharacter } from '../../context/CharacterContext';
import {
  classifyCharacterField,
  formatCharacterField,
  getCharacterErrorCode,
  type Character,
} from '../../services/api/CharacterService';

interface CharacterRosterSidebarProps {
  campaignId: string;
  isGM: boolean;
  currentUserId?: string;
  /** Authorized roster and request state supplied by the campaign's CharacterContext. */
  characters: Character[];
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
  onCharacterClick?: (character: Character) => void;
  onAddCharacterClick?: () => void;
  onVisibilitySettingsClick?: (character: Character) => void;
}

export const CharacterRosterSidebar: React.FC<CharacterRosterSidebarProps> = ({
  campaignId,
  characters,
  isLoading,
  error,
  onRetry,
  onCharacterClick,
  onAddCharacterClick,
  onVisibilitySettingsClick
}) => {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [lifecycleError, setLifecycleError] = useState<string | null>(null);
  const { archivedCharacters, archivedLoading, archivedMessage, loadArchivedCharacters, restoreCharacter } = useCharacter();

  // Detect mobile view
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Only the explicit server projection grants owner formatting/controls.
  // Missing ownership flags remain ordinary party entries, regardless of order.
  const playerCharacter = characters.find((character) => character.is_owner === true);
  const partyMembers = characters.filter((character) => character.is_owner !== true);

  // Sidebar width
  const sidebarWidth = isCollapsed ? '60px' : '280px';

  // Mobile drawer styles
  if (isMobile) {
    return (
      <>
        {/* Mobile Toggle Button */}
        <button
          onClick={() => setIsMobileOpen(!isMobileOpen)}
          style={{
            position: 'fixed',
            top: '80px',
            right: '16px',
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            backgroundColor: '#d4af37',
            border: 'none',
            color: '#1a1a1a',
            fontSize: '20px',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            zIndex: 999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          👥
        </button>

        {/* Mobile Drawer Overlay */}
        {isMobileOpen && (
          <div
            onClick={() => setIsMobileOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1000
            }}
          />
        )}

        {/* Mobile Drawer */}
        <div
          style={{
            position: 'fixed',
            top: 0,
            right: isMobileOpen ? 0 : '-300px',
            width: '300px',
            height: '100%',
            backgroundColor: '#1a1a1a',
            borderLeft: '2px solid #d4af37',
            transition: 'right 0.3s ease',
            zIndex: 1001,
            overflowY: 'auto',
            padding: '20px'
          }}
        >
          {renderContent()}
        </div>
      </>
    );
  }

  // Desktop Sidebar
  return (
    <div
      style={{
        position: 'fixed',
        top: '64px',
        right: 0,
        width: sidebarWidth,
        height: 'calc(100vh - 64px)',
        backgroundColor: '#1a1a1a',
        borderLeft: '2px solid #333',
        transition: 'width 0.3s ease',
        overflowY: 'auto',
        overflowX: 'hidden',
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Collapse Toggle */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        style={{
          position: 'absolute',
          top: '12px',
          left: '12px',
          width: '32px',
          height: '32px',
          borderRadius: '4px',
          backgroundColor: '#2a2a2a',
          border: '1px solid #444',
          color: '#d4af37',
          cursor: 'pointer',
          fontSize: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        title={isCollapsed ? 'Expand' : 'Collapse'}
      >
        {isCollapsed ? '→' : '←'}
      </button>

      {!isCollapsed && renderContent()}
    </div>
  );

  function renderContent() {
    if (isLoading) {
      return (
        <div style={{ padding: '20px', color: '#888', textAlign: 'center' }}>
          Loading roster...
        </div>
      );
    }

    if (error) {
      return (
        <div style={{ padding: '20px', color: '#c41e3a', textAlign: 'center' }}>
          <div>{error}</div>
          <button
            onClick={onRetry}
            style={{
              marginTop: '12px',
              padding: '8px 16px',
              backgroundColor: '#d4af37',
              color: '#1a1a1a',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Retry
          </button>
        </div>
      );
    }

    return (
      <>
        {/* Player's Character (Large Display) */}
        {playerCharacter && (
          <div style={{ padding: '20px', paddingTop: '60px' }}>
            <div style={{
              fontSize: '14px',
              color: '#d4af37',
              fontWeight: '600',
              marginBottom: '12px',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              Your Character
            </div>

            <div style={{
              backgroundColor: '#2a2a2a',
              border: '2px solid #d4af37',
              borderRadius: '8px',
              padding: '16px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
              onClick={() => onCharacterClick && onCharacterClick(playerCharacter)}
            >
              {/* Character Name & Class */}
              <div style={{
                fontSize: '18px',
                color: '#d4af37',
                fontWeight: 'bold',
                marginBottom: '4px'
              }}>
                {formatCharacterField(classifyCharacterField(playerCharacter.character_name, playerCharacter.is_owner === true), (v) => v)}
              </div>
              <div style={{
                fontSize: '14px',
                color: '#aaa',
                marginBottom: '12px'
              }}>
                {formatCharacterField(classifyCharacterField(playerCharacter.character_class, playerCharacter.is_owner === true), (v) => v)}
                {' • Level '}
                {formatCharacterField(classifyCharacterField(playerCharacter.level, playerCharacter.is_owner === true), (v) => String(v))}
              </div>

              {/* HP Bar — this block is the OWNER's own character, so
                  classifyCharacterField's isOwner=true: null always means
                  "not set," never "hidden" (the owner is never redacted
                  from their own data, per the R6 backend's owner_payload()).
                  hp===0 renders as 0, not "Not set" — classifyCharacterField
                  only treats `null`/`undefined` as absent, never a real 0. */}
              {(() => {
                const hpDisplay = classifyCharacterField(playerCharacter.hp, playerCharacter.is_owner === true);
                const maxHpDisplay = classifyCharacterField(playerCharacter.max_hp, playerCharacter.is_owner === true);
                const hasBothForBar = hpDisplay.kind === 'value' && maxHpDisplay.kind === 'value' && (maxHpDisplay.value as number) > 0;
                return (
                  <div style={{ marginBottom: '8px' }}>
                    <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>
                      HP: {formatCharacterField(hpDisplay, (v) => String(v))}/{formatCharacterField(maxHpDisplay, (v) => String(v))}
                    </div>
                    {hasBothForBar && (
                      <div style={{ width: '100%', height: '8px', backgroundColor: '#444', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{
                          width: `${((hpDisplay.value as number) / (maxHpDisplay.value as number)) * 100}%`,
                          height: '100%',
                          backgroundColor: (hpDisplay.value as number) < (maxHpDisplay.value as number) / 2 ? '#c41e3a' : '#4caf50',
                          transition: 'width 0.3s ease'
                        }} />
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* AC */}
              <div style={{ fontSize: '12px', color: '#888' }}>
                AC: {formatCharacterField(
                  classifyCharacterField(playerCharacter.armor_class, playerCharacter.is_owner === true),
                  (v) => String(v)
                )}
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '8px',
                marginTop: '12px'
              }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onVisibilitySettingsClick && onVisibilitySettingsClick(playerCharacter);
                  }}
                  style={{
                    flex: 1,
                    padding: '6px',
                    fontSize: '12px',
                    backgroundColor: '#333',
                    color: '#d4af37',
                    border: '1px solid #d4af37',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  👁️ Visibility
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/character/${playerCharacter.character_id}`);
                  }}
                  style={{
                    flex: 1,
                    padding: '6px',
                    fontSize: '12px',
                    backgroundColor: '#333',
                    color: '#d4af37',
                    border: '1px solid #d4af37',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Edit
                </button>
              </div>
            </div>
          </div>
        )}

        {/* The server scopes this list to the current member and their own archived rows. */}
        <div style={{ padding: '0 20px 16px' }}>
          <button aria-expanded={showArchived} onClick={() => {
            const next = !showArchived;
            setShowArchived(next);
            if (next) void loadArchivedCharacters(campaignId);
          }} style={{ width: '100%', padding: '8px', background: '#222', color: '#d4af37', border: '1px solid #555', borderRadius: '4px' }}>
            {showArchived ? 'Hide archived characters' : 'My archived characters'}
          </button>
          {showArchived && archivedLoading && <div role="status" style={{ padding: '10px', color: '#aaa' }}>Loading archived characters…</div>}
          {showArchived && archivedMessage && <div role="alert" style={{ padding: '10px', color: '#f88' }}>{archivedMessage}</div>}
          {showArchived && !archivedLoading && !archivedMessage && archivedCharacters.length === 0 && <div style={{ padding: '10px', color: '#888' }}>No archived characters.</div>}
          {showArchived && archivedCharacters.map((character) => character.is_owner === true && character.is_archived === true && (
            <div key={character.character_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', padding: '8px 0', color: '#ddd' }}>
              <span>{character.character_name ?? 'Unnamed character'}</span>
              <button onClick={() => {
                setLifecycleError(null);
                void restoreCharacter(character.character_id, campaignId).catch((error) => {
                  setLifecycleError(getCharacterErrorCode(error) === 'active_character_exists'
                    ? 'Restore is unavailable while you have another active character in this campaign.'
                    : error instanceof Error ? error.message : 'Could not restore character.');
                });
              }}>Restore</button>
            </div>
          ))}
          {showArchived && lifecycleError && <div role="alert" style={{ padding: '8px', color: '#f88' }}>{lifecycleError}</div>}
        </div>

        {/* Divider */}
        <div style={{
          height: '1px',
          backgroundColor: '#333',
          margin: '0 20px'
        }} />

        {/* Party Members */}
        <div style={{ padding: '20px' }}>
          <div style={{
            fontSize: '14px',
            color: '#aaa',
            fontWeight: '600',
            marginBottom: '12px',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>
            Party Members
          </div>

          {partyMembers.length === 0 ? (
            <div style={{
              fontSize: '14px',
              color: '#666',
              fontStyle: 'italic',
              textAlign: 'center',
              marginBottom: '16px'
            }}>
              {playerCharacter ? 'No other party members yet' : 'No party members yet'}
            </div>
          ) : (
            <div style={{ marginBottom: '16px' }}>
              {partyMembers.map((character) => (
                <div
                  key={character.character_id}
                  onClick={() => onCharacterClick && onCharacterClick(character)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    backgroundColor: '#2a2a2a',
                    borderRadius: '6px',
                    marginBottom: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    border: '1px solid transparent'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#333';
                    e.currentTarget.style.borderColor = '#d4af37';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#2a2a2a';
                    e.currentTarget.style.borderColor = 'transparent';
                  }}
                >
                  {/* Mini Avatar/Icon */}
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#444',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px',
                    flexShrink: 0
                  }}>
                    🧙
                  </div>

                  {/* Character Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '14px',
                      color: '#fff',
                      fontWeight: '600',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {formatCharacterField(classifyCharacterField(character.character_name, false), (v) => v)}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#888'
                    }}>
                      {formatCharacterField(classifyCharacterField(character.character_class, false), (v) => v)}
                      {' • Lvl '}
                      {formatCharacterField(classifyCharacterField(character.level, false), (v) => String(v))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add Character Button */}
          <button
            onClick={onAddCharacterClick}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#2a2a2a',
              border: '2px dashed #d4af37',
              borderRadius: '6px',
              color: '#d4af37',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#333';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#2a2a2a';
            }}
          >
            <span style={{ fontSize: '18px' }}>+</span>
            Add Character
          </button>
        </div>

        {/* [FUTURE CODESPACE] Persistent during active session */}
        {/* This sidebar will become sticky across pages when session is active */}
      </>
    );
  }
};

